/**
 * Экран входа (auth-and-roles «Вход по электронной почте и паролю»). Подписи — по адресу входа
 * (team-zone-login-page): на внутреннем адресе — «Вход для сотрудников Центрики» и ссылка на
 * клиентский кабинет вместо регистрации физлица; на внешнем — «Личный кабинет клиента». Пока адрес
 * не известен — нейтральная подпись без ссылок.
 */
import { useState, type FormEvent } from 'react'
import { useAuth } from '@/app/auth-context'
import { useEntryZone } from '@/app/entry-zone'
import { Link } from '@/design-system/components/primitives/link'
import { AppLink } from '@/app/navigation'
import { AuthShell } from './auth-shell'
import { Field } from '@/design-system/components/primitives/field'
import { Input } from '@/design-system/components/primitives/input'
import { PasswordInput } from '@/design-system/components/primitives/password-input'
import { Button } from '@/design-system/components/primitives/button'
import { Alert } from '@/design-system/components/feedback/alert'
import { getErrorMessage } from '@/lib/errors'

export function LoginPage() {
  const { login } = useAuth()
  const zone = useEntryZone()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await login({ email, password })
      // При успехе AuthProvider переводит сессию в authenticated → редирект из /login уводит
      // отсюда; сбрасывать submitting не нужно, но finally делает поток единообразным с reset/settings.
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось войти'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AuthShell
      subtitle={
        zone === null
          ? 'Личный кабинет'
          : zone.zone === 'team'
            ? 'Вход для сотрудников Центрики'
            : 'Личный кабинет клиента'
      }
      footer={
        zone === null ? null : zone.zone === 'team' ? (
          zone.clientUrl ? (
            <span className="text-txt-secondary">
              Вы клиент? <Link href={zone.clientUrl}>Личный кабинет клиента</Link>
            </span>
          ) : null
        ) : (
          <span className="text-txt-secondary">
            Нет аккаунта? <AppLink to="/register">Регистрация физлица</AppLink>
          </span>
        )
      }
    >
      <form onSubmit={onSubmit} noValidate>
        {error ? (
          <Alert variant="danger" className="mb-4">
            {error}
          </Alert>
        ) : null}
        <Field required label="Электронная почта">
          {(props) => (
            <Input
              {...props}
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          )}
        </Field>
        <Field required label="Пароль">
          {(props) => (
            <PasswordInput
              {...props}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          )}
        </Field>
        <Button type="submit" className="mt-2 w-full justify-center" disabled={submitting}>
          {submitting ? 'Вход…' : 'Войти'}
        </Button>
        <div className="mt-4 text-center text-13">
          <AppLink to="/reset">Забыли пароль?</AppLink>
        </div>
      </form>
    </AuthShell>
  )
}
